import React, { useEffect, useState } from 'react';
import { getLocalizedShortcutLabel, normalizeLanguage } from '../utils/i18n';

const ICON_EXTENSIONS = ['svg', 'png', 'icns'];
const SPECIAL_ICON_IDS = {
  auth: 'ai-auth-default',
  chat: 'chat-text-default'
};

const resolveIconSource = (iconValue, extension = 'svg') => `/icons/${iconValue}.${extension}`;

const ICON_SOURCE_FALLBACKS = {
  settings: ['/icons/settings-icon.svg'],
  profile: [],
  resume: ['/icons/resume-icon.svg'],
  projects: ['/icons/projects-folder.svg'],
  github: ['/icons/github-icon.svg'],
  docker: ['/icons/docker.png']
};

const ICON_EMOJI_FALLBACKS = {
  settings: '⚙️',
  profile: '👤',
  resume: '📄',
  projects: '🗂️',
  'ai-auth': '🔐',
  'ai-chat': '💬',
  github: '🐙',
  docker: '🐳'
};

const Icon = ({
  icon,
  language,
  onOpen,
  variant = 'dock',
  registerDockNode,
  isBouncing = false,
  isMinimized = false,
  isProcessing = false,
  onDockTooltipChange
}) => {
  const [keywordExtensionIndex, setKeywordExtensionIndex] = useState(0);
  const [sourceIndex, setSourceIndex] = useState(0);
  const activeLanguage = normalizeLanguage(language);
  const localizedLabel = getLocalizedShortcutLabel(icon, activeLanguage);
  const isDockVariant = variant === 'dock';
  const isDesktopVariant = variant === 'desktop';

  useEffect(() => {
    setKeywordExtensionIndex(0);
    setSourceIndex(0);
  }, [icon.icon, icon.id]);

  const fallbackSources = ICON_SOURCE_FALLBACKS[icon.id] || [];
  const emojiFallback = ICON_EMOJI_FALLBACKS[icon.id] || '?';

  const renderSpecialIcon = (iconValue) => {
    if (iconValue === SPECIAL_ICON_IDS.auth) {
      return (
        <div className="special-shortcut-icon special-shortcut-icon-auth" aria-hidden="true">
          <div className="special-shortcut-auth-core">
            <span className="special-shortcut-auth-mark">C</span>
          </div>
          <span className="special-shortcut-auth-badge">AI</span>
        </div>
      );
    }

    if (iconValue === SPECIAL_ICON_IDS.chat) {
      return (
        <div className="special-shortcut-icon special-shortcut-icon-chat" aria-hidden="true">
          <span className="special-shortcut-chat-label">Chat</span>
        </div>
      );
    }

    return null;
  };

  const renderIconContent = (iconValue) => {
    const specialIcon = renderSpecialIcon(iconValue);
    if (specialIcon) {
      return specialIcon;
    }

    if (iconValue.includes('/') || iconValue.includes('.')) {
      const imageSources = [iconValue, ...fallbackSources.filter((source) => source !== iconValue)];
      const activeSource = imageSources[sourceIndex];

      if (!activeSource) {
        return <span className="emoji-icon">{emojiFallback}</span>;
      }

      return (
        <img
          src={activeSource}
          alt={localizedLabel}
          draggable="false"
          onError={() => {
            setSourceIndex((prev) => prev + 1);
          }}
        />
      );
    }

    const isKeyword = /^[a-zA-Z0-9-_]+$/.test(iconValue);

    if (isKeyword) {
      if (sourceIndex > 0) {
        const fallbackSource = fallbackSources[sourceIndex - 1];

        if (fallbackSource) {
          return (
            <img
              src={fallbackSource}
              alt={localizedLabel}
              draggable="false"
              onError={() => {
                setSourceIndex((prev) => prev + 1);
              }}
            />
          );
        }

        return <span className="emoji-icon">{emojiFallback}</span>;
      }

      const extension = ICON_EXTENSIONS[keywordExtensionIndex];
      return (
        <img
          src={resolveIconSource(iconValue, extension)}
          alt={localizedLabel}
          draggable="false"
          onError={() => {
            if (keywordExtensionIndex < ICON_EXTENSIONS.length - 1) {
              setKeywordExtensionIndex((prev) => prev + 1);
              return;
            }

            setSourceIndex((prev) => prev + 1);
          }}
        />
      );
    }

    return <span className="emoji-icon">{iconValue || emojiFallback}</span>;
  };

  return (
    <button
      type="button"
      ref={(node) => {
        if (registerDockNode) {
          registerDockNode(icon.id, node);
        }
      }}
      className={`desktop-icon ${isDockVariant ? 'desktop-icon-dock' : ''} ${isDesktopVariant ? 'desktop-icon-shortcut' : ''} ${isBouncing ? 'dock-icon-bounce' : ''} ${isMinimized ? 'dock-icon-minimized' : ''} ${isProcessing ? 'dock-icon-processing' : ''}`.trim()}
      onClick={() => {
        if (!isProcessing) {
          onOpen(icon.id);
        }
      }}
      onMouseEnter={(event) => {
        if (isDockVariant && onDockTooltipChange) {
          onDockTooltipChange({
            id: icon.id,
            label: localizedLabel,
            node: event.currentTarget
          });
        }
      }}
      onMouseMove={(event) => {
        if (isDockVariant && onDockTooltipChange) {
          onDockTooltipChange({
            id: icon.id,
            label: localizedLabel,
            node: event.currentTarget
          });
        }
      }}
      onMouseLeave={() => {
        if (isDockVariant && onDockTooltipChange) {
          onDockTooltipChange(null);
        }
      }}
      onFocus={(event) => {
        if (isDockVariant && onDockTooltipChange) {
          onDockTooltipChange({
            id: icon.id,
            label: localizedLabel,
            node: event.currentTarget
          });
        }
      }}
      onBlur={() => {
        if (isDockVariant && onDockTooltipChange) {
          onDockTooltipChange(null);
        }
      }}
      aria-label={localizedLabel}
    >
      <div className={`icon-img ${isDockVariant ? 'icon-img-dock' : ''}`.trim()}>
        {renderIconContent(icon.icon)}
        {isProcessing ? (
          <span className="icon-processing-ring" aria-hidden="true">
            <img src="/logo192.png" alt="" draggable="false" />
          </span>
        ) : null}
      </div>
      {isDockVariant ? null : (
        <div className="icon-label">
          <span>{localizedLabel}</span>
        </div>
      )}
    </button>
  );
};

export default Icon;
